import { useQuery } from '@tanstack/react-query';
import type { ID } from '@proofroom/schema';
import { isAgency, useSession } from '../auth/session';
import { documents, plugin } from '../api/endpoints';

/** Formats an ISO instant in UTC so the label is stable regardless of the viewer's locale. */
function formatBuiltAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()} ${hh}:${mm}`;
}

/** Short relative label for the plugin's last known presence: seconds, then minutes, hours, days. */
function formatRelative(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
}

/** Whether the current session should see the rail; screens use this to pick the page class. */
export function useIsAgency(): boolean {
  const { user } = useSession();
  return isAgency(user);
}

/** Left-hand rail on the list screens; only agency designers and admins see it. */
export function AgencyRail({ documentId }: { documentId?: ID } = {}) {
  const { user } = useSession();
  const eligible = isAgency(user);
  const q = useQuery({ queryKey: ['plugin', 'version'], queryFn: plugin.version, enabled: eligible, retry: false });
  const statusQuery = useQuery({
    queryKey: ['documents', documentId, 'plugin-status'],
    queryFn: () => documents.pluginStatus(documentId as ID),
    enabled: eligible && Boolean(documentId),
    retry: false,
  });
  if (!eligible) return null;

  const versionLine = q.data ? `Version ${q.data.version}, built ${formatBuiltAt(q.data.builtAt)}` : 'Version unavailable';

  const status = statusQuery.data;
  const presenceLine = documentId
    ? status?.connected
      ? `InDesign: connected as ${status.userName}`
      : status?.lastSeenAt
        ? `InDesign: last seen ${formatRelative(status.lastSeenAt)}`
        : 'InDesign: not connected'
    : undefined;

  return (
    <aside className="rail">
      <div className="card">
        <h3>Designer tools</h3>
        <a className="btn primary" href="/api/plugin/download">Download InDesign plugin</a>
        <p className="muted small">{versionLine}</p>
        {presenceLine && <p className="muted small">{presenceLine}</p>}
        <ol className="steps">
          <li>Download the plugin.</li>
          <li>Double-click the downloaded .ccx file.</li>
          <li>Creative Cloud Desktop installs it; choose Install if warned about an unverified developer.</li>
          <li>In InDesign, open it via Window &gt; Plugins &gt; Proofroom.</li>
        </ol>
      </div>
    </aside>
  );
}
